<template lang="pug">
  .push-notifications
    p.push-notifications__description
      | Control which push notifications are available in your program. Turn notification
      | types on or off for all participants. Individuals may adjust their personal
      | notification preferences based on what&apos;s enabled here.
    BLoading(:active="!settingsLoaded" :is-full-page="false")
    template(v-if="settingsLoaded")
      .notif-category(
        v-for="(categoryData, categoryKey) in settings"
        :key="categoryKey"
      )
        .notif-category__name {{ getCategoryLabel(categoryKey) }}
        .notif-type-row(
          v-for="(channels, typeKey) in categoryData.types"
          :key="typeKey"
        )
          .notif-type-row__info
            p.notif-type-row__title {{ getTypeLabel(categoryKey, typeKey) }}
            p.notif-type-row__desc {{ getTypeDescription(categoryKey, typeKey) }}
          .notif-type-row__toggle
            span.toggle-off Off
            BSwitch(
              :value="channels.fcm"
              type="is-info"
              passive-type="is-light"
              @input="handleToggle(categoryKey, typeKey, 'fcm', $event)"
            )
            span.toggle-on On
</template>

<script>
import { mapGetters, mapActions } from "vuex";

const NOTIFICATION_META = {
  challenges: {
    label: "Challenges",
    types: {
      starting_soon: {
        title: "Challenge starting soon",
        description:
          "Sends a reminder 24 hours before a challenge begins to encourage participants to get ready."
      },
      started: {
        title: "Challenge started",
        description:
          "Sends a reminder 24 hours after a challenge begins to encourage participants to join."
      },
      midway_reminder: {
        title: "Midway challenge reminder",
        description:
          "Sends a motivational check-in at the halfway point to encourage continued participation."
      },
      ending_soon: {
        title: "Challenge ending soon",
        description:
          "Sends a reminder 48 hours before a challenge ends, prompting participants to complete final activities."
      },
      ended: {
        title: "Challenge has ended",
        description:
          "Notifies participants that the challenge is complete and invites them to view results."
      },
      discussion_reply: {
        title: "New discussion board post",
        description:
          "Alerts participants when new posts are added to the challenge discussion, encouraging engagement and conversation."
      }
    }
  },
  social: {
    label: "Social",
    types: {
      team_invitation: {
        title: "Team invitation",
        description:
          "Notifies individuals when they've been invited to join a team and prompts them to accept the invitation."
      },
      friend_request: {
        title: "Friend request",
        description:
          "Notifies individuals when someone sends them a friend request and prompts them to accept or view the request."
      }
    }
  },
  content: {
    label: "Content",
    types: {
      daily_wellness_guide: {
        title: "Daily wellness guide",
        description:
          "Delivers the daily wellness plan at a scheduled time to support consistent routines and engagement."
      }
    }
  },
  workshops: {
    label: "Workshops",
    types: {
      upcoming: {
        title: "Upcoming workshops",
        description:
          "Notifies individuals about upcoming workshops and encourages them to reserve a spot."
      },
      reminder: {
        title: "Workshop reminder",
        description:
          "Sends a reminder 24 hours before a registered workshop begins to reduce no-shows and support attendance."
      }
    }
  }
};

export default {
  name: "AdminPushNotifications",

  computed: {
    ...mapGetters("pushNotificationSettings", ["settings", "settingsLoaded"])
  },

  mounted() {
    if (!this.settingsLoaded) {
      this.fetchPushNotificationSettings();
    }
  },

  methods: {
    ...mapActions("pushNotificationSettings", [
      "fetchPushNotificationSettings",
      "updatePushNotificationTypeDefault"
    ]),

    getCategoryLabel(categoryKey) {
      return NOTIFICATION_META[categoryKey]?.label || categoryKey;
    },

    getTypeLabel(categoryKey, typeKey) {
      return NOTIFICATION_META[categoryKey]?.types[typeKey]?.title || typeKey;
    },

    getTypeDescription(categoryKey, typeKey) {
      return NOTIFICATION_META[categoryKey]?.types[typeKey]?.description || "";
    },

    typeCount(categoryData) {
      return Object.keys(categoryData.types || {}).length;
    },

    async handleToggle(category, type, channel, enabled) {
      try {
        await this.updatePushNotificationTypeDefault({
          category,
          type,
          channel,
          enabled
        });
      } catch {
        this.$buefy.toast.open({
          message: "Failed to update setting. Please try again.",
          type: "is-danger"
        });
        await this.fetchPushNotificationSettings();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.push-notifications {
  padding: 0.5rem 1.25rem 1.5rem;

  &__description {
    color: #555;
    font-size: 0.875rem;
    margin-bottom: 1.25rem;
  }
}

.notif-category {
  &__name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #363636;
    margin-top: 0.75rem;
    margin-bottom: 0.25rem;
  }
}

.notif-type-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;

  &__info {
    flex: 1;
    padding-right: 1rem;
  }

  &__title {
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 0.1rem;
  }

  &__desc {
    font-size: 0.78rem;
    color: #888;
    line-height: 1.3;
  }

  &__toggle {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    white-space: nowrap;
  }
}

.toggle-off,
.toggle-on {
  font-size: 0.78rem;
  color: #666;
}
</style>
